// src/pages/LandingPage.tsx

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  FolderKanban, 
  Users, 
  Calendar, 
  Download,
  BarChart3,
  Zap,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Sparkles,
  ArrowRight,
  Play,
  Shield,
  Globe,
  Gift,
  UserPlus,
} from 'lucide-react';

// スクロールアニメーション用Hook
const useScrollAnimation = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

// アニメーション付きセクション
const AnimatedSection = ({ 
  children, 
  className = '', 
  delay = 0 
}: { 
  children: React.ReactNode; 
  className?: string;
  delay?: number;
}) => {
  const { ref, isVisible } = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        transitionDelay: `${delay}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
      }}
    >
      {children}
    </div>
  );
};

// FAQアイテム
const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left hover:text-blue-600 transition-colors"
      >
        <span className="text-lg font-semibold text-gray-800">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-5' : 'max-h-0'
        }`}
      >
        <p className="text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

// 機能カード
const FeatureCard = ({ 
  icon: Icon, 
  title, 
  description,
  delay = 0
}: { 
  icon: any; 
  title: string; 
  description: string;
  delay?: number;
}) => (
  <AnimatedSection delay={delay}>
    <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-1">
      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white group-hover:scale-110 transition-transform">
          <Icon className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </AnimatedSection>
);

// 課題カード
const ProblemCard = ({ 
  icon: Icon, 
  title, 
  description,
  delay = 0
}: { 
  icon: any; 
  title: string; 
  description: string;
  delay?: number;
}) => (
  <AnimatedSection delay={delay}>
    <div className="bg-gray-50 p-8 rounded-2xl">
      <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-orange-400 via-pink-500 to-blue-500 text-white mb-5">
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  </AnimatedSection>
);

export const LandingPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      image: '/images/tsimage.png',
      title: 'ワンクリックで、正確な時間記録',
      description: '「出勤」「退勤」ボタンを押すだけのシンプル操作。プロジェクト未選択でも出勤でき、後から紐付けも可能です。',
    },
    {
      image: '/images/summaryimage.png',
      title: '充実のレポート機能',
      description: 'プロジェクトごとの稼働実績や収支をひと目で把握。CSV・PDFエクスポートで請求書作成や確定申告にも活用できます。',
    },
    {
      image: '/images/calimage.png',
      title: 'Googleカレンダー連携',
      description: '普段お使いのGoogleカレンダーの予定を、ワンクリックで稼働実績としてインポート。二重管理から解放されます。',
    },
  ];

  // 自動スライド
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const faqs = [
    {
      question: '無料で利用できますか？',
      answer: 'はい、Freeプランは完全無料でご利用いただけます。プロジェクト5件まで、取引先5件までの制限がありますが、基本的な打刻・レポート機能はすべてお使いいただけます。',
    },
    {
      question: 'Standardプランの料金は？',
      answer: '月額980円（税込）でご利用いただけます。プロジェクト・取引先無制限、PDFエクスポート、Googleカレンダー連携、広告非表示など、すべての機能をお使いいただけます。',
    },
    {
      question: 'データはどのように活用できますか？',
      answer: '記録したデータは、プロジェクトごとのレポートで可視化できるほか、CSVやPDF形式でエクスポートできます。請求書作成時の稼働報告や、確定申告時の資料としてご活用いただけます。',
    },
    {
      question: '打刻を忘れた場合、後から修正できますか？',
      answer: 'はい、可能です。サマリーページから過去の日時に遡って、稼働時間の追加や修正を簡単に行うことができます。',
    },
    {
      question: 'チーム（複数人）で利用できますか？',
      answer: 'いいえ、sidepocketは個人向けのサービスです。現時点でチーム利用は想定しておりません。',
    },
    {
      question: 'データは安全に保管されますか？',
      answer: 'はい、すべてのデータはGoogle Firebaseの安全なクラウド環境で暗号化して保管されます。',
    },
  ];

  return (
    <div className="bg-white text-gray-800">
      {/* ヘッダー */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/images/sidepocket_Logo.png"
                alt="sidepocket Logo"
                className="h-8 w-auto"
              />
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <nav>
                <ul className="flex items-center gap-8">
                  <li>
                    <a href="#features" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
                      機能
                    </a>
                  </li>
                  <li>
                    <a href="#pricing" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
                      料金プラン
                    </a>
                  </li>
                  <li>
                    <a href="#faq" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
                      よくある質問
                    </a>
                  </li>
                </ul>
              </nav>
              
              <div className="flex items-center gap-4">
                <Link
                  to="/login"
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  ログイン
                </Link>
                <Link
                  to="/signup"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg shadow-lg shadow-blue-500/30 transition-all hover:scale-105"
                >
                  無料で始める
                </Link>
              </div>
            </div>

            {/* モバイル用 */}
            <div className="md:hidden flex items-center gap-3">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600"
              >
                ログイン
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg"
              >
                無料で始める
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* ヒーローセクション */}
        <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
          {/* 背景デコレーション */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200/30 rounded-full -mr-48 -mt-48 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-purple-200/30 rounded-full -ml-36 -mb-36 blur-3xl" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid md:grid-cols-2 gap-12 items-center py-16 sm:py-24">
              <AnimatedSection>
                <img
                  src="/images/sidepocket_Logo.png"
                  alt="sidepocket Logo"
                  className="h-20 w-auto mb-6"
                />
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight mb-6">
                  フリーランス・副業の
                  <br />
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    稼働管理をスマートに
                  </span>
                </h1>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  誰でもカンタンに使える稼働管理アプリ。
                  <br />
                  煩わしい時間集計やレポート作成を自動化し、
                  <br />
                  あなたの働き方をスマートにサポートします。
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/signup"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 transition-all hover:scale-105"
                  >
                    今すぐ無料で試す
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <a
                    href="#features"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-xl shadow-lg border border-gray-200 transition-all hover:scale-105"
                  >
                    <Play className="w-5 h-5" />
                    機能を見る
                  </a>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={200}>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-3xl blur-2xl" />
                  <img
                    src="/images/summaryMOC.png"
                    alt="sidepocketのサマリー画面"
                    className="relative rounded-2xl shadow-2xl"
                  />
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* 課題セクション */}
        <section className="py-20 sm:py-28 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
                  こんな時間の悩みを抱えていませんか？
                </h2>
                <p className="text-lg text-gray-600">
                  フリーランスや副業でよくある、時間管理の課題
                </p>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-8">
              <ProblemCard
                icon={BarChart3}
                title="稼働時間の集計が大変"
                description="プロジェクトごとに稼働時間を集計し、請求書作成や確定申告のための稼働報告書を作成するのが月末の大きな負担に。"
                delay={100}
              />
              <ProblemCard
                icon={FolderKanban}
                title="プロジェクト管理が煩雑"
                description="複数の案件を抱えていると、稼働実績、収益管理、契約期間、単価など、管理すべきことが多すぎて把握しきれない。"
                delay={200}
              />
              <ProblemCard
                icon={Zap}
                title="最適なツールがない"
                description="勤怠システムは法人向けが多く、フリーランス・副業ワーカーが使いやすいシンプルなツールがなかなか見つからない。"
                delay={300}
              />
            </div>
          </div>
        </section>

        {/* 機能紹介スライダー */}
        <section id="features" className="py-20 sm:py-28 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
                  sidepocketが、すべて解決します
                </h2>
                <p className="text-lg text-gray-600">
                  あなたの時間管理をシンプルにする、直感的でパワフルな機能
                </p>
              </div>
            </AnimatedSection>

            {/* スライダー */}
            <div className="max-w-6xl mx-auto mb-20">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <AnimatedSection>
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl" />
                    <img
                      src={slides[currentSlide].image}
                      alt={slides[currentSlide].title}
                      className="relative rounded-2xl shadow-xl transition-opacity duration-500"
                    />
                  </div>
                </AnimatedSection>

                <AnimatedSection delay={100}>
                  <div className="space-y-6">
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      {slides[currentSlide].title}
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {slides[currentSlide].description}
                    </p>
                    
                    {/* スライドインジケーター */}
                    <div className="flex gap-2 pt-4">
                      {slides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSlide(index)}
                          className={`w-3 h-3 rounded-full transition-all ${
                            currentSlide === index
                              ? 'bg-blue-600 w-8'
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </AnimatedSection>
              </div>
            </div>

            {/* 機能グリッド */}
            <AnimatedSection>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">
                機能一覧
              </h3>
            </AnimatedSection>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <FeatureCard
                icon={Clock}
                title="ワンクリック打刻"
                description="シンプルな操作で日々の稼働時間を正確に記録。プロジェクト未選択でも出勤OK。"
                delay={0}
              />
              <FeatureCard
                icon={FolderKanban}
                title="プロジェクト管理"
                description="案件ごとの契約情報や単価をまとめて管理。進行中・完了・アーカイブで整理。"
                delay={50}
              />
              <FeatureCard
                icon={Users}
                title="取引先・担当者管理"
                description="取引先と担当者の情報を一元管理。プロジェクトとの紐付けも簡単。"
                delay={100}
              />
              <FeatureCard
                icon={BarChart3}
                title="収益・稼働管理"
                description="月々の稼働時間や収益をグラフで可視化。働きすぎ防止にも。"
                delay={150}
              />
              <FeatureCard
                icon={Calendar}
                title="カレンダー表示"
                description="月・週・日ビューで稼働実績を確認。直感的なUIで一目瞭然。"
                delay={200}
              />
              <FeatureCard
                icon={Download}
                title="CSV/PDFエクスポート"
                description="稼働実績をCSVやPDFで簡単出力。クライアント報告や確定申告に。"
                delay={250}
              />
              <FeatureCard
                icon={Globe}
                title="Googleカレンダー連携"
                description="カレンダーの予定をワンクリックで稼働実績としてインポート。"
                delay={300}
              />
              <FeatureCard
                icon={Shield}
                title="安全なデータ管理"
                description="Google Firebaseで安全にデータを保管。いつでもどこでもアクセス可能。"
                delay={350}
              />
            </div>
          </div>
        </section>

        {/* 料金プラン */}
        <section id="pricing" className="py-20 sm:py-28 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
                  あなたに合った料金プラン
                </h2>
                <p className="text-lg text-gray-600">
                  まずは無料で始めて、必要に応じてアップグレード
                </p>
              </div>
            </AnimatedSection>

            <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Freeプラン */}
              <AnimatedSection delay={100}>
                <div className="bg-white border-2 border-gray-200 rounded-3xl p-8 h-full flex flex-col shadow-lg">
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
                    <p className="text-gray-600">まずは無料で始めたい方に</p>
                    <div className="mt-6">
                      <span className="text-5xl font-extrabold text-gray-900">¥0</span>
                      <span className="text-gray-600 ml-2">/月</span>
                    </div>
                  </div>

                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 mb-4">含まれる機能</p>
                    <ul className="space-y-3">
                      {[
                        'ワンクリック打刻',
                        'プロジェクト管理（5件まで）',
                        '取引先管理（5件まで）',
                        'カレンダー表示',
                        'サマリー・レポート',
                        'CSVエクスポート（基本項目）',
                      ].map((item) => (
                        <li key={item} className="flex items-center gap-3">
                          <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-green-600" />
                          </div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                      {[
                        'Googleカレンダー連携',
                        'PDFエクスポート',
                        '広告非表示',
                      ].map((item) => (
                        <li key={item} className="flex items-center gap-3 text-gray-400">
                          <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <X className="w-3 h-3 text-gray-400" />
                          </div>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to="/signup"
                    className="mt-8 block w-full py-4 text-center bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg"
                  >
                    無料でアカウント作成
                  </Link>
                </div>
              </AnimatedSection>

              {/* Standardプラン */}
              <AnimatedSection delay={200}>
                <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-500 rounded-3xl p-8 h-full flex flex-col shadow-xl">
                  {/* おすすめバッジ */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 bg-blue-600 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-1">
                      <Sparkles className="w-4 h-4" />
                      おすすめ
                    </span>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-blue-900 mb-2">Standard</h3>
                    <p className="text-blue-700">本格的に活用したい方向け</p>
                    <div className="mt-6">
                      <span className="text-5xl font-extrabold text-gray-900">¥980</span>
                      <span className="text-gray-600 ml-2">/月</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      年払いなら ¥9,800/年（2ヶ月分お得）
                    </p>
                  </div>

                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 mb-4">含まれる機能</p>
                    <ul className="space-y-3">
                      {[
                        'Freeプランの全機能',
                        'プロジェクト無制限',
                        '取引先・担当者無制限',
                        'Googleカレンダー連携',
                        'PDFエクスポート',
                        'CSVエクスポート（全項目）',
                        '広告非表示',
                        '優先サポート',
                      ].map((item) => (
                        <li key={item} className="flex items-center gap-3">
                          <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-blue-600" />
                          </div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to="/signup"
                    className="mt-8 block w-full py-4 text-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-blue-500/30"
                  >
                    Standardで始める
                  </Link>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

{/* 招待特典セクション */}
<section className="py-20 sm:py-28 bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50">
  <div className="container mx-auto px-4 sm:px-6 lg:px-8">
    <AnimatedSection>
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
          <Gift className="w-4 h-4" />
          友達紹介プログラム
        </div>
        
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
          友達を招待して、<br />
          <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
            お互いにStandardプランをゲット
          </span>
        </h2>
        
        <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
          あなたの招待コードを友達に共有するだけ。
          友達がsidepocketに登録すると、お互いにStandardプランが無料で使えます。
        </p>

        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* 招待される側 */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-orange-100">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              招待された方
            </h3>
            <div className="text-4xl font-extrabold text-orange-500 mb-2">
              30日間無料
            </div>
            <p className="text-gray-600 text-sm">
              Standardプランのすべての機能が30日間無料で使えます
            </p>
          </div>

          {/* 招待する側 */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-purple-100">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              招待した方
            </h3>
            <div className="text-4xl font-extrabold text-purple-500 mb-2">
              60日間無料
            </div>
            <p className="text-gray-600 text-sm">
              お礼としてStandardプランが60日間延長されます
            </p>
          </div>
        </div>

        <div className="mt-12">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold rounded-xl shadow-lg shadow-orange-500/30 hover:scale-105 transition-all"
          >
            今すぐ登録して招待コードをゲット
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </AnimatedSection>
  </div>
</section>

        {/* CTA */}
        <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
            <AnimatedSection>
            <div className="text-center text-white max-w-3xl mx-auto">
                <h2 className="text-3xl sm:text-4xl font-extrabold mb-6">
                さあ、sidepocketで働き方を変えよう
                </h2>
                <p className="text-xl text-blue-100 mb-10">
                もう面倒な時間管理に悩まされるのは終わり。
                <br />
                今すぐ登録して、新しい働き方を始めましょう。
                </p>
                <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-10 py-4 bg-white text-blue-600 font-bold rounded-xl shadow-xl hover:scale-105 transition-all"
                >
                アカウントを無料で作成
                <ChevronRight className="w-5 h-5" />
                </Link>
            </div>
            </AnimatedSection>
        </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 sm:py-28 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
                  よくあるご質問
                </h2>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-8">
                {faqs.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>
      </main>

      {/* フッター */}
      <footer className="bg-gray-900 text-gray-400">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center">
            <img
              src="/images/sidepocket_Logo_w.png"
              alt="sidepocket Logo"
              className="h-8 w-auto mb-6"
            />
            <div className="flex gap-8 mb-6">
              <Link to="/terms" className="hover:text-white transition-colors">
                利用規約
              </Link>
              <Link to="/privacy" className="hover:text-white transition-colors">
                プライバシーポリシー
              </Link>
              <Link to="/contact" className="hover:text-white transition-colors">
                お問い合わせ
              </Link>
            </div>
            <p className="text-sm">
              &copy; {new Date().getFullYear()} sidepocket. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};